import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import { KeyboardBackspace } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { getRegionByCurrency } from '@luxuryescapes/lib-regions';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import { usePromo } from '~/hooks/usePromo';

import { CreateOrUpdatePromoPayload, InternalPromoWithDiscounts, ensureCodeNameIsValid } from '~/services/PromoService';
import { addDays, formatDateLongISO } from '~/services/TimeService';

import Spinner from '../Common/Spinner';
import { CategoryTagSelector, DepartmentTagSelector, SubCategoryTagSelector } from '../PromoV3/PromoTagSelectors';
import { withTenant } from '../hoc';

import PromoDiscountCards from './PromoDiscountCards';

interface Props {
  tenant: App.Tenant;
  user: App.User;
}

const CURRENCIES = [
  'AUD',
  'CAD',
  'CNY',
  'EUR',
  'HKD',
  'INR',
  'IDR',
  'ILS',
  'JPY',
  'KRW',
  'MOP',
  'MYR',
  'NZD',
  'PHP',
  'QAR',
  'RUB',
  'SAR',
  'SGD',
  'TWD',
  'THB',
  'ZAR',
  'AED',
  'GBP',
  'USD',
  'VND',
];

function SimplePromoForm({ tenant }: Props) {
  const { user: currentUser, isLoading } = useCurrentUser();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [createdPromo, setCreatedPromo] = useState<null | InternalPromoWithDiscounts>(null);

  const [formData, setFormData] = useState({
    code_name: '',
    description: '',
    promo_type: 'percentage',
    max_discount: 5000,
    min_spend: 0,
    code_limit: 10,
    limit_per_user: 1,
    currency: 'AUD',
    products: ['hotel-hotel', 'hotel-tactical_ao_hotel', 'tour-ALL', 'cruise-ALL'],
    expires_at: formatDateLongISO(addDays(365)),
    dept_tag: '',
    category_tag: '',
    sub_category_tag: '',
    promoDiscounts: [
      {
        id: 1,
        discountValue: 10,
        products: ['hotel-hotel'],
      },
      {
        id: 2,
        discountValue: 10,
        products: ['hotel-bedbank_hotel', 'hotel-tactical_ao_hotel'],
      },
      {
        id: 4,
        discountValue: 10,
        products: ['tour-ALL', 'cruise-ALL'],
      },
    ],
  });

  const [highlightedFields, setHighlightedFields] = useState<
    Array<'code_name' | 'description' | 'add_discounts' | 'dept_tag' | 'category_tag' | 'sub_category_tag' | string>
  >([]);

  const resetHighlightedField = (field: string) => setHighlightedFields(highlightedFields.filter((f) => f !== field));

  // Focus management function
  const focusElementByName = (name: string) => {
    const element: HTMLElement | null = document.querySelector(`[name="${name}"]`);
    if (element) {
      element.focus();
    }
  };

  // Scroll to element function with dynamic lookup
  const scrollToElement = (fieldName: string) => {
    // Custom logic for discount cards
    if (fieldName.startsWith('d-')) {
      const discountId = fieldName.replace('d-', '');
      const discountCard = document.querySelector(`[data-discount-id="${discountId}"]`);
      if (discountCard) {
        discountCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    // General element lookup by name
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Fallback: try to find by id
    const elementById = document.getElementById(fieldName);
    if (elementById) {
      elementById.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Special case for add_discounts
    if (fieldName === 'add_discounts') {
      const addButton = document.querySelector('[data-add-discounts-button]');
      if (addButton) {
        addButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
  };

  // Effect to scroll to highlighted fields
  useEffect(() => {
    if (highlightedFields.length > 0) {
      // Scroll to the first highlighted field
      const firstHighlightedField = highlightedFields[0];
      scrollToElement(firstHighlightedField);
    }
  }, [highlightedFields]);

  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();

  const {
    errors,
    createPromo,
    initPromoData,
    isPromoMetaLoading,
    promoMeta,
    promoTagArrays,
    promoTagSelections,
    setPromoTagSelection,
    savePromoCreateTemplate,
  } = usePromo({
    options: {
      autoSelectTag: true,
    },
    onPromoUpdate: async (promo) => {
      if (!promo) {
        return;
      }
      enqueueSnackbar(`${brand}: Promo code created.`, { variant: 'success' });
      setCreatedPromo(promo);
    },
    onPromoError: (error, field) => {
      if (field) {
        setHighlightedFields([field]);
      }
      enqueueSnackbar(error, { variant: 'warning' });
    },
    onTagSelectionChange: (field, value) => {
      // Handle focus management when tag selections change
      if (value && field === 'dept_tag' && promoTagArrays?.categoryTags.length > 1) {
        setTimeout(() => focusElementByName('category_tag'), 50);
      } else if (value && field === 'category_tag' && promoTagArrays?.subCategoryTags.length > 1) {
        setTimeout(() => focusElementByName('sub_category_tag'), 50);
      }
    },
  });

  const resetTagErrorHighlighting = () => {
    resetHighlightedField('dept_tag');
    resetHighlightedField('category_tag');
    resetHighlightedField('sub_category_tag');
  };

  const handleChange = (
    field:
      | 'code_name'
      | 'description'
      | 'dept_tag'
      | 'category_tag'
      | 'sub_category_tag'
      | 'currency'
      | 'promo_type'
      | 'max_discount'
      | 'min_spend'
      | 'code_limit'
      | 'limit_per_user'
      | 'expires_at'
      | 'promoDiscounts',
    value: any,
  ) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    switch (field) {
      case 'code_name': {
        const codeError = ensureCodeNameIsValid(value);
        if (codeError == null) {
          resetHighlightedField('code_name');
        } else {
          setHighlightedFields(['code_name']);
        }
        break;
      }
      case 'description': {
        if (!value) {
          setHighlightedFields(['description']);
        } else {
          resetHighlightedField('description');
        }
        break;
      }
      case 'dept_tag': {
        resetTagErrorHighlighting();
        break;
      }
      case 'category_tag': {
        resetTagErrorHighlighting();
        break;
      }
      case 'sub_category_tag': {
        resetTagErrorHighlighting();
        break;
      }
    }
  };

  useEffect(() => {
    if (!hasLoaded) {
      initPromoData(null);
      setHasLoaded(true);
    }
  }, [hasLoaded]);

  // Update description when user data becomes available
  useEffect(() => {
    if (currentUser && !isLoading) {
      setFormData((prev) => ({
        ...prev,
        description: `Promo created by ${currentUser.givenName} ${currentUser.surname}`,
      }));
    }
  }, [currentUser, isLoading]);

  // Sync tag selections with form data
  useEffect(() => {
    if (promoTagSelections) {
      setFormData((prev) => ({
        ...prev,
        dept_tag: promoTagSelections.selectedDepartment || '',
        category_tag: promoTagSelections.selectedCategory || '',
        sub_category_tag: promoTagSelections.selectedSubCategory || '',
      }));
    }
  }, [promoTagSelections]);

  const onSubmit = async () => {
    const {
      code_name,
      description,
      currency,
      promo_type,
      expires_at,
      code_limit,
      limit_per_user,
      min_spend,
      max_discount,
    } = formData;

    const codeErr = ensureCodeNameIsValid(code_name);
    if (codeErr !== null) {
      setHighlightedFields(['code_name']);
      enqueueSnackbar(codeErr, { variant: 'warning' });
      return;
    }

    if (description.length == 0) {
      setHighlightedFields(['description']);
      enqueueSnackbar('Promo code requires a description', { variant: 'warning' });
      return;
    }

    // Validate tag fields
    if (!formData.dept_tag || formData.dept_tag.length === 0) {
      setHighlightedFields(['dept_tag']);
      enqueueSnackbar('Please select a Department', { variant: 'warning' });

      return;
    }

    if (promoTagArrays?.categoryTags.length > 0 && (!formData.category_tag || formData.category_tag.length === 0)) {
      setHighlightedFields(['category_tag']);
      enqueueSnackbar('Please select a Category', { variant: 'warning' });
      return;
    }

    if (
      promoTagArrays?.subCategoryTags.length > 0 &&
      (!formData.sub_category_tag || formData.sub_category_tag.length === 0)
    ) {
      setHighlightedFields(['sub_category_tag']);
      enqueueSnackbar('Please select a Sub-Category', { variant: 'warning' });
      return;
    }

    const promoDiscounts = formData.promoDiscounts;

    if (!promoDiscounts || promoDiscounts?.length == 0) {
      setHighlightedFields(['add_discounts']);
      enqueueSnackbar('Add at least one discount to create this promo', { variant: 'warning' });
      return;
    }

    const invalidDiscounts = promoDiscounts.filter((d) => !d.products || d.products.length === 0);
    if (invalidDiscounts.length > 0) {
      enqueueSnackbar('Each discount needs at least one product type.', { variant: 'error' });
      setHighlightedFields(invalidDiscounts.map((d) => `d-${d.id}`));
      return;
    }

    setHighlightedFields([]);

    try {
      const transformedDiscounts: CreateOrUpdatePromoPayload['promoDiscounts'] = promoDiscounts.map((discount) => ({
        discount_value: discount.discountValue,
        region: getRegionByCurrency(currency).code as CreateOrUpdatePromoPayload['promoDiscounts'][number]['region'],
        products: (discount.products || []) as CreateOrUpdatePromoPayload['promoDiscounts'][number]['products'],
        min_spend: min_spend,
        max_discount: promo_type === 'percentage' ? max_discount : undefined,
      }));

      const payload: CreateOrUpdatePromoPayload = {
        promo: {
          code_name,
          promo_type: promo_type as unknown as CreateOrUpdatePromoPayload['promo']['promo_type'],
          description,
          expires_at,
          code_limit,
          brand: tenant.brand as unknown as CreateOrUpdatePromoPayload['promo']['brand'],
          device_types: [],
          promo_toggles: ['allow-with-membership-pricing'],
          limit_per_user: limit_per_user,
          limit_per_user_per_year: null,
          dept_tag: formData.dept_tag,
          category_tag: formData.category_tag,
          sub_category_tag: formData.sub_category_tag,
        },
        promoDiscounts: transformedDiscounts,
        mode: 'create-only',
      };

      await createPromo(payload);
      // Save template after successful promo creation
      savePromoCreateTemplate();
    } catch (e) {
      console.error('Failed to create promo', e);
    }
  };

  const handleCopyToClipboard = (codeName: string) => {
    if (codeName) {
      try {
        navigator.clipboard.writeText(codeName);
        enqueueSnackbar('Promo code copied!', { variant: 'success' });
      } catch (e) {
        console.warn('Failed to copy promo code to clipboard', { codeName, e });
      }
    }
  };

  if (createdPromo) {
    return (
      <Container maxWidth="sm">
        <Helmet>
          <title>Promo Code Created</title>
        </Helmet>
        <Stack spacing={4} alignItems="center" justifyContent="center" minHeight="400px">
          <Alert variant="standard">{`Promo '${createdPromo.code_name}' created`}</Alert>
          <Typography variant="h4">{createdPromo.code_name}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="outlined"
              onClick={() => handleCopyToClipboard(createdPromo.code_name)}
              startIcon={<ContentCopyIcon />}
            >
              Copy to clipboard
            </Button>
            <Button variant="contained" onClick={() => push(`/promos/${createdPromo.id_promo_code}`)}>
              Edit Promo
            </Button>
          </Stack>
          <Divider />
          <Button variant="contained" onClick={() => setCreatedPromo(undefined)} startIcon={<KeyboardBackspace />}>
            Create Another Promo Code
          </Button>
        </Stack>
      </Container>
    );
  }

  if (isPromoMetaLoading) {
    return (
      <Container maxWidth="sm">
        <Helmet>
          <title>Create Promo Code</title>
        </Helmet>
        <Stack spacing={4} alignItems="center" justifyContent="center" minHeight="400px">
          <Spinner />
          {isPromoMetaLoading && <Typography variant="body1">Loading promo configuration...</Typography>}
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Helmet>
        <title>Create Promo Code</title>
      </Helmet>
      <Grid container spacing={3} mt={1}>
        {errors && errors.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="error">{JSON.stringify(errors, null, 2)}</Alert>
          </Grid>
        )}

        <Grid item xs={6}>
          <TextField
            name="code_name"
            label="Code"
            fullWidth
            value={formData.code_name}
            error={highlightedFields.includes('code_name')}
            onChange={(e) => {
              handleChange('code_name', e.target.value?.toUpperCase());
            }}
            autoFocus
            required
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={formData.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              label="Currency"
            >
              {CURRENCIES.map((cur) => (
                <MenuItem key={cur} value={cur}>
                  {cur}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="description"
            label="Description"
            required
            fullWidth
            multiline
            rows={1}
            error={highlightedFields.includes('description')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Enter promo description..."
          />
        </Grid>

        {promoTagArrays && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <DepartmentTagSelector
                promoTagArrays={promoTagArrays}
                promoTagSelections={promoTagSelections}
                setPromoTagSelection={setPromoTagSelection}
                isPromoMetaLoading={isPromoMetaLoading}
                formType="rjsf"
                formData={formData}
                onFormChange={(evt) => {
                  if (evt.formData) {
                    handleChange('dept_tag', evt.formData.dept_tag);
                  }
                }}
                isErroring={highlightedFields.includes('dept_tag')}
              />
              <CategoryTagSelector
                promoTagArrays={promoTagArrays}
                promoTagSelections={promoTagSelections}
                setPromoTagSelection={setPromoTagSelection}
                isPromoMetaLoading={isPromoMetaLoading}
                formType="rjsf"
                formData={formData}
                onFormChange={(evt) => {
                  if (evt.formData) {
                    handleChange('category_tag', evt.formData.category_tag);
                  }
                }}
                isErroring={highlightedFields.includes('category_tag')}
              />
              <SubCategoryTagSelector
                promoTagArrays={promoTagArrays}
                promoTagSelections={promoTagSelections}
                setPromoTagSelection={setPromoTagSelection}
                isPromoMetaLoading={isPromoMetaLoading}
                isErroring={highlightedFields.includes('sub_category_tag')}
                formType="rjsf"
                formData={formData}
                onFormChange={(evt) => {
                  if (evt.formData) {
                    handleChange('sub_category_tag', evt.formData.sub_category_tag);
                  }
                }}
              />
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControl>
            <FormLabel>Type</FormLabel>
            <RadioGroup row value={formData.promo_type} onChange={(e) => handleChange('promo_type', e.target.value)}>
              <FormControlLabel value="percentage" control={<Radio />} label="Percentage" />
              <FormControlLabel value="fixed_amount" control={<Radio />} label="Fixed Amount" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <PromoDiscountCards
            value={formData.promoDiscounts}
            onChange={(newValue) => handleChange('promoDiscounts', newValue)}
            promoType={formData.promo_type}
            promoMeta={promoMeta}
            showAddButton={!createdPromo}
            highlightedFields={highlightedFields}
            resetHighlightedField={resetHighlightedField}
          />
        </Grid>

        {formData.promo_type === 'percentage' && (
          <Grid item xs={6}>
            <TextField
              name="max_discount"
              label="Max Discount"
              type="number"
              fullWidth
              inputProps={{ min: 0 }}
              value={formData.max_discount}
              onChange={(e) => handleChange('max_discount', parseFloat(e.target.value))}
            />
          </Grid>
        )}
        <Grid item xs={6} md={formData.promo_type === 'percentage' ? 6 : 6}>
          <TextField
            name="min_spend"
            label="Min Spend"
            type="number"
            fullWidth
            value={formData.min_spend}
            inputProps={{ min: 0 }}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (value >= 0 || isNaN(value)) {
                handleChange('min_spend', value);
              }
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            name="code_limit"
            label="Max Uses"
            type="number"
            inputProps={{ min: 0 }}
            fullWidth
            value={formData.code_limit}
            onChange={(e) => handleChange('code_limit', parseInt(e.target.value, 10))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="limit_per_user"
            label="Max Uses per Customer"
            type="number"
            fullWidth
            value={formData.limit_per_user}
            onChange={(e) => handleChange('limit_per_user', parseInt(e.target.value, 10))}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="expires_at"
            label="Expiry"
            type="datetime-local"
            fullWidth
            value={formData.expires_at.slice(0, 16)}
            onChange={(e) => handleChange('expires_at', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Box textAlign="right">
            <Button variant="contained" onClick={onSubmit}>
              Create Promo Code
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export default withTenant(SimplePromoForm);
